import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTradeGroupAdjustments, AdjustmentLogEntry } from '../api/tradeGroups';
import './AdjustmentLogTable.css';

interface AdjustmentLogTableProps {
  tradeGroupId: string;
}

const AdjustmentLogTable: React.FC<AdjustmentLogTableProps> = ({ tradeGroupId }) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [adjustmentTypeFilter, setAdjustmentTypeFilter] = useState<string>('');
  const [expandedReasons, setExpandedReasons] = useState<Set<number>>(new Set());

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trade-group-adjustments', tradeGroupId, page, perPage, adjustmentTypeFilter],
    queryFn: () => getTradeGroupAdjustments(tradeGroupId, page, perPage, adjustmentTypeFilter || undefined),
    staleTime: 30000, // Cache for 30 seconds
  });

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPriceRange = (min: number | null, max: number | null): string => {
    if (min === null && max === null) return '-';
    if (min !== null && max !== null) return `${formatCurrency(min)} - ${formatCurrency(max)}`;
    if (min !== null) return `From ${formatCurrency(min)}`;
    if (max !== null) return `To ${formatCurrency(max)}`;
    return '-';
  };

  const getAdjustmentTypeBadge = (type: string): JSX.Element => {
    const config = {
      RANGE_CREATED: { label: 'Range Created', className: 'badge-created' },
      RANGE_AGED_OUT: { label: 'Range Aged Out', className: 'badge-aged' },
      CAPITAL_REDEPLOYED: { label: 'Capital Redeployed', className: 'badge-redeployed' },
    };

    const { label, className } = config[type as keyof typeof config] || { label: type, className: 'badge-default' };

    return <span className={`adjustment-badge ${className}`}>{label}</span>;
  };

  const toggleReasonExpansion = (logId: number) => {
    setExpandedReasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const renderReason = (entry: AdjustmentLogEntry): JSX.Element => {
    const isLong = entry.reason.length > 100;
    const isExpanded = expandedReasons.has(entry.logId);

    if (!isLong) {
      return <span className="reason-text">{entry.reason}</span>;
    }

    return (
      <div className="reason-container">
        <span className="reason-text">
          {isExpanded ? entry.reason : `${entry.reason.substring(0, 100)}...`}
        </span>
        <button
          className="reason-toggle"
          onClick={() => toggleReasonExpansion(entry.logId)}
          type="button"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      </div>
    );
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAdjustmentTypeFilter(e.target.value);
    setPage(1); // Reset to first page when filtering
  };

  if (isLoading) {
    return (
      <div className="adjustment-log-loading">
        <div className="spinner"></div>
        <p>Loading adjustment log...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="adjustment-log-error">
        <div className="error-icon">⚠️</div>
        <p className="error-message">Failed to load adjustment log</p>
        <p className="error-detail">{error instanceof Error ? error.message : 'Unknown error'}</p>
        <button className="retry-button" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const adjustments = data?.adjustments || [];
  const pagination = data?.pagination;

  if (adjustments.length === 0 && !adjustmentTypeFilter) {
    return (
      <div className="adjustment-log-empty">
        <div className="empty-icon">📝</div>
        <p className="empty-message">No adjustments yet</p>
        <p className="empty-description">
          Adjustments will appear here when the bot creates ranges, ages them out, or redeploys capital.
        </p>
      </div>
    );
  }

  return (
    <div className="adjustment-log-container">
      {/* Filter Controls */}
      <div className="adjustment-log-controls">
        <div className="filter-group">
          <label htmlFor="adjustmentTypeFilter">Filter by Type:</label>
          <select
            id="adjustmentTypeFilter"
            value={adjustmentTypeFilter}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="RANGE_CREATED">Range Created</option>
            <option value="RANGE_AGED_OUT">Range Aged Out</option>
            <option value="CAPITAL_REDEPLOYED">Capital Redeployed</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {adjustments.length === 0 ? (
        <div className="adjustment-log-empty">
          <div className="empty-icon">🔍</div>
          <p className="empty-message">No adjustments match the selected filter</p>
          <p className="empty-description">Try selecting a different adjustment type.</p>
        </div>
      ) : (
        <>
          <div className="adjustment-log-table-wrapper">
            <table className="adjustment-log-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Type</th>
                  <th>Price Range</th>
                  <th>Capital Amount</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((entry) => (
                  <tr key={entry.logId}>
                    <td className="timestamp-cell">{formatTimestamp(entry.createdAt)}</td>
                    <td className="type-cell">{getAdjustmentTypeBadge(entry.adjustmentType)}</td>
                    <td className="price-range-cell">{formatPriceRange(entry.priceRangeMin, entry.priceRangeMax)}</td>
                    <td className="capital-cell">{formatCurrency(entry.capitalAmount)}</td>
                    <td className="reason-cell">{renderReason(entry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => setPage(page - 1)}
                disabled={!pagination.hasPreviousPage}
                type="button"
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
              </span>
              <button
                className="pagination-button"
                onClick={() => setPage(page + 1)}
                disabled={!pagination.hasNextPage}
                type="button"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdjustmentLogTable;
